import React, { useEffect, useRef, useState } from 'react';
import { pinDialogFrame, scrollPinFieldIntoView, type VisualViewportBox } from './pin-dialog-frame';

interface PinDialogProps {
  titleId: string;
  title: string;
  description: string;
  pin: string;
  onPinChange: (pin: string) => void;
  error: string | null;
  busy: boolean;
  /** Keeps the confirm button off even when a PIN is typed (lockout). */
  submitDisabled?: boolean;
  confirmLabel: string;
  busyLabel: string;
  onSubmit: (event: React.FormEvent) => void;
  onClose: () => void;
}

function readViewport(): { viewport: VisualViewportBox | null; layoutHeight: number } {
  if (typeof window === 'undefined') return { viewport: null, layoutHeight: 800 };
  const viewport = window.visualViewport;
  return {
    viewport: viewport ? { height: viewport.height, offsetTop: viewport.offsetTop } : null,
    layoutHeight: window.innerHeight,
  };
}

/**
 * Shared PIN prompt. It is pinned to the top of the visual viewport and scrolls,
 * so the on-screen keyboard does not cover the field.
 */
export function PinDialog({
  titleId,
  title,
  description,
  pin,
  onPinChange,
  error,
  busy,
  submitDisabled = false,
  confirmLabel,
  busyLabel,
  onSubmit,
  onClose,
}: PinDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [frame, setFrame] = useState(() => {
    const current = readViewport();
    return pinDialogFrame(current.viewport, current.layoutHeight);
  });

  useEffect(() => {
    const update = () => {
      const current = readViewport();
      setFrame(pinDialogFrame(current.viewport, current.layoutHeight));
    };
    update();
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.focus();
    const timer = window.setTimeout(() => scrollPinFieldIntoView(node), 50);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const node = inputRef.current;
    if (node && document.activeElement === node) scrollPinFieldIntoView(node);
  }, [frame.height, frame.offsetTop]);

  return (
    <div
      data-pin-dialog=""
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-x-0 z-[60] flex max-h-[100dvh] items-start justify-center overflow-y-auto px-4 pb-4 pt-3"
      style={{ top: frame.offsetTop, height: frame.height, maxHeight: frame.height }}
    >
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Cancel" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className="relative mb-4 w-full max-w-md space-y-4 overflow-y-auto rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-5 py-5 shadow-[var(--shadow-lg)]"
      >
        <div>
          <h2 id={titleId} className="text-lg font-semibold text-[var(--color-text-primary)]">
            {title}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{description}</p>
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">PIN</span>
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={12}
            value={pin}
            onChange={(event) => onPinChange(event.target.value.replace(/\D/g, ''))}
            onFocus={(event) => scrollPinFieldIntoView(event.currentTarget)}
            aria-label="PIN"
            aria-invalid={Boolean(error)}
            className={`h-12 w-full rounded-[var(--radius-md)] border bg-[var(--color-surface-1)] px-3 text-center text-xl tracking-[0.4em] text-[var(--color-text-primary)] ${
              error ? 'border-[var(--color-error)]' : 'border-[var(--color-border-strong)]'
            }`}
          />
        </label>
        {error ? (
          <p className="text-sm font-medium text-[var(--color-error-text)]" role="alert">
            {error}
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] text-sm font-medium text-[var(--color-text-primary)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || submitDisabled || pin.length === 0}
            className="min-h-12 rounded-[var(--radius-md)] bg-[var(--color-error)] text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
